import {
  FileText,
  Minus,
  ArrowUp,
  ArrowDown,
  Plus,
  TrendingUp,
  Check,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  ProductFromXML,
  NewProductData,
} from '../../../services/purchases.service'
import { CatalogSelect } from '../../ui/CatalogSelect'

interface ReviewModalProps {
  showReviewModal: boolean
  setShowReviewModal: (show: boolean) => void
  extractedProducts: ProductFromXML[]
  newProductsData: NewProductData[]
  setNewProductsData: (data: NewProductData[]) => void
  confirmProductEntry: () => void
  setShowUploadModal: (show: boolean) => void
}

export function ReviewModal({
  showReviewModal,
  setShowReviewModal,
  extractedProducts,
  newProductsData,
  setNewProductsData,
  confirmProductEntry,
  setShowUploadModal,
}: ReviewModalProps) {
  return (
    <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            Revisión de Productos - Comparación de Precios
          </DialogTitle>
          <DialogDescription className="sr-only">
            Revisar productos extraídos del XML y comparar cambios de precios
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Summary */}
          <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-5 w-5 text-primary" />
              <div className="flex-1">
                <h3 className="mb-1 font-medium text-primary/90">
                  {extractedProducts.length} productos encontrados en el XML
                </h3>
                {extractedProducts.length > 0 && (
                  <p className="mb-3 text-sm text-primary">
                    Proveedor:{' '}
                    <span className="font-semibold">
                      {extractedProducts[0].supplier}
                    </span>
                  </p>
                )}
                <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-primary">Incrementos:</p>
                    <p className="font-semibold text-primary/90">
                      {extractedProducts.filter((p) => p.priceChange > 0).length}{' '}
                      productos
                    </p>
                  </div>
                  <div>
                    <p className="text-primary">Decrementos:</p>
                    <p className="font-semibold text-primary/90">
                      {extractedProducts.filter((p) => p.priceChange < 0).length}{' '}
                      productos
                    </p>
                  </div>
                  <div>
                    <p className="text-primary">Sin cambio:</p>
                    <p className="font-semibold text-primary/90">
                      {
                        extractedProducts.filter((p) => p.priceChange === 0)
                          .length
                      }{' '}
                      productos
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Cant.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Precio Anterior
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Precio Nuevo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Cambio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Precio Venta
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Utilidad %
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Lote
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {extractedProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {product.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          SAT: {product.satKey}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900">
                        {product.quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-700">
                        ${product.previousPrice.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-slate-900">
                        ${product.newPrice.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {product.priceChange === 0 ? (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Minus className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Sin cambio
                          </span>
                        </div>
                      ) : product.priceChange > 0 ? (
                        <div className="flex items-center gap-1 text-red-600">
                          <ArrowUp className="h-4 w-4" />
                          <span className="text-sm font-semibold">
                            +{product.priceChange.toFixed(2)}%
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-green-600">
                          <ArrowDown className="h-4 w-4" />
                          <span className="text-sm font-semibold">
                            {product.priceChange.toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900">
                        ${product.salePrice.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900">
                        {product.margin.toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-600">
                        {product.lotNumber}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* New Products Registration */}
          {newProductsData.length > 0 && (
            <div className="space-y-4">
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                <div className="flex items-start gap-3">
                  <Plus className="mt-0.5 h-5 w-5 text-orange-600" />
                  <div>
                    <h4 className="font-medium text-orange-900">
                      {newProductsData.length} producto
                      {newProductsData.length > 1 ? 's' : ''} no registrado
                      {newProductsData.length > 1 ? 's' : ''}
                    </h4>
                    <p className="mt-1 text-sm text-orange-800">
                      Los siguientes productos no están en el inventario.
                      Completa la información para registrarlos.
                    </p>
                  </div>
                </div>
              </div>

              {newProductsData.map((product, index) => (
                <div
                  key={product.id}
                  className="rounded-lg border-2 border-orange-200 bg-orange-50/50 p-4"
                >
                  <h5 className="mb-4 flex items-center gap-2 font-medium text-slate-900">
                    <Badge
                      variant="outline"
                      className="border-orange-300 bg-orange-100 text-orange-700"
                    >
                      Nuevo
                    </Badge>
                    {product.name}
                  </h5>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Categoría <span className="text-red-600">*</span>
                      </label>
                      <CatalogSelect
                        catalog="categories"
                        value={product.categoryId}
                        onChange={(value) => {
                          const updated = [...newProductsData]
                          updated[index].categoryId = value
                          setNewProductsData(updated)
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Departamento <span className="text-red-600">*</span>
                      </label>
                      <CatalogSelect
                        catalog="departments"
                        value={product.departmentId}
                        onChange={(value) => {
                          const updated = [...newProductsData]
                          updated[index].departmentId = value
                          setNewProductsData(updated)
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Tipo de Producto <span className="text-red-600">*</span>
                      </label>
                        <CatalogSelect
                          catalog="product_types"
                          returnObject={true}
                          value={product.productTypeId}
                          onChange={(obj: any) => {
                            const updated = [...newProductsData]
                            updated[index].productTypeId = obj?.id ? String(obj.id) : obj
                            updated[index].requiresLot = Boolean(obj?.requires_lot)
                            setNewProductsData(updated)
                          }}
                        />
                    </div>

                    {product.requiresLot && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Fecha de Caducidad{' '}
                          <span className="text-red-600">*</span>
                        </label>
                        <Input
                          type="date"
                          value={product.expiryDate}
                          onChange={(e) => {
                            const updated = [...newProductsData]
                            updated[index].expiryDate = e.target.value
                            setNewProductsData(updated)
                          }}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        Tipo de Unidad
                      </label>
                      <CatalogSelect
                        catalog="units"
                        value={product.unitId}
                        onChange={(value) => {
                          const updated = [...newProductsData]
                          updated[index].unitId = value
                          setNewProductsData(updated)
                        }}
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-slate-700">
                        Descripción
                      </label>
                      <Input
                        value={product.description}
                        onChange={(e) => {
                          const updated = [...newProductsData]
                          updated[index].description = e.target.value
                          setNewProductsData(updated)
                        }}
                        placeholder="Descripción del producto"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Price Changes Alert */}
          {extractedProducts.some((p) => Math.abs(p.priceChange) > 10) && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-start gap-3">
                <TrendingUp className="mt-0.5 h-5 w-5 text-yellow-600" />
                <div>
                  <h4 className="font-medium text-yellow-900">
                    Cambios significativos detectados
                  </h4>
                  <p className="mt-1 text-sm text-yellow-800">
                    Algunos productos tienen cambios de precio superiores al
                    10%. Revisa los precios antes de confirmar.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={confirmProductEntry}
              disabled={newProductsData.some(
                (p) =>
                  !p.categoryId ||
                  !p.departmentId ||
                  (p.requiresLot && !p.expiryDate),
              )}
              className="flex-1"
              size="lg"
            >
              <Check className="mr-2 h-5 w-5" />
              Confirmar Ingreso de Productos
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowReviewModal(false)
                setShowUploadModal(true)
                setNewProductsData([])
              }}
              className="flex-1"
              size="lg"
            >
              <X className="mr-2 h-5 w-5" />
              Cancelar y Volver
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
